import i18n from "@/lib/i18n";
import { showOkCancelDialog, showNotifyDialog } from "@/lib/dialog";
import router from "@/router";
import { usePageEditStore } from "@/stores/pageEdit";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Amplify / axios 系エラーから HTTP ステータスを取り出す。 */
function extractHttpStatus(error: unknown): number | null {
  if (!isRecord(error) || !("response" in error)) return null;

  const { response } = error;
  if (!isRecord(response)) return null;

  const { status, statusCode } = response;
  if (typeof status === "number") return status;
  if (typeof statusCode === "number") return statusCode;
  return null;
}

/** rule-based API が 422 を返したときのレスポンス JSON から `title` を取り出す。 */
export function extractRuleBased422Title(error: unknown): string | null {
  if (extractHttpStatus(error) !== 422) return null;
  if (!isRecord(error) || !("response" in error)) return null;

  const { response } = error;
  if (!isRecord(response)) return null;

  const { data: rawData } = response;
  let data: unknown = rawData;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!isRecord(data) || typeof data.title !== "string") return null;

  const title = data.title.trim();
  return title.length > 0 ? title : null;
}

export const PROOFREADING_NEW_RUN_ERROR_CODES = {
  RESUME_RUNNING: "PROOFREADING_RESUME_RUNNING",
  RESUME_COMPLETED: "PROOFREADING_RESUME_COMPLETED",
  WORK_EXISTS: "PROOFREADING_WORK_EXISTS",
} as const;

function extractProblemPayload(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error) || !("response" in error)) return null;
  const { response } = error;
  if (!isRecord(response)) return null;
  let {data} = response;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  return isRecord(data) ? data : null;
}

export function extractProofreadingErrorCode(error: unknown): string | null {
  const data = extractProblemPayload(error);
  if (!data || typeof data.error_code !== "string") return null;
  const code = data.error_code.trim();
  return code.length > 0 ? code : null;
}

function messageForNewRunConflict(error: unknown): string {
  const code = extractProofreadingErrorCode(error);
  if (code === PROOFREADING_NEW_RUN_ERROR_CODES.RESUME_RUNNING) {
    return i18n.t("proofreadingSelectTypePage.conflictRunning") as string;
  }
  if (code === PROOFREADING_NEW_RUN_ERROR_CODES.RESUME_COMPLETED) {
    return i18n.t("proofreadingSelectTypePage.conflictCompleted") as string;
  }
  if (code === PROOFREADING_NEW_RUN_ERROR_CODES.WORK_EXISTS) {
    return i18n.t("proofreadingSelectTypePage.conflictWork") as string;
  }
  const data = extractProblemPayload(error);
  if (data && typeof data.title === "string" && data.title.trim().length > 0) {
    return data.title.trim();
  }
  return i18n.t("proofreadingSelectTypePage.conflictGeneric") as string;
}

/**
 * 新規校正 API の 409（途中タスクあり）を処理する。
 * ダイアログ表示後にタイプ選択へ戻し true。それ以外は false。
 */
export async function handleProofreadingNewRun409Error(
  error: unknown,
  novelId: string,
): Promise<boolean> {
  if (extractHttpStatus(error) !== 409) return false;

  await showNotifyDialog({
    content: messageForNewRunConflict(error),
    okButton: i18n.t("proofreadingApplyConfirmDialog.ok") as string,
  });

  usePageEditStore().setModified(false);
  await router.replace({
    name: "proofreadingSelectType",
    params: { novelId },
  });
  return true;
}

/** rule-based API が 402 を返したかどうか（Fish 残高不足）。 */
export function isRuleBased402Error(error: unknown): boolean {
  return extractHttpStatus(error) === 402;
}

/**
 * rule-based API の 422 を処理する。
 * 422 + title ならダイアログ表示後に原稿選択画面へ遷移し true。
 * それ以外は false（呼び出し元で通常エラー処理）。
 */
export async function handleRuleBased422Error(
  error: unknown,
  novelId: string,
): Promise<boolean> {
  const title = extractRuleBased422Title(error);
  if (title == null) return false;

  await showNotifyDialog({
    content: title,
    okButton: i18n.t("proofreadingApplyConfirmDialog.ok") as string,
  });

  usePageEditStore().setModified(false);
  await router.push({
    name: "proofreadingSelectManuscript",
    params: { novelId },
  });

  return true;
}

/**
 * rule-based API の 402（Fish 残高不足）を処理する。
 * 校正画面に留まり、購入ページは別タブで開く（作業内容を破棄しない）。
 * それ以外は false（呼び出し元で通常エラー処理）。
 */
export async function handleRuleBased402Error(
  error: unknown,
  _novelId: string,
): Promise<boolean> {
  if (!isRuleBased402Error(error)) return false;

  const shouldOpenPurchase = await showOkCancelDialog({
    content: i18n.t("proofreadingReviseManuscriptPage.fishInsufficient") as string,
    okButton: i18n.t("proofreadingFishConfirmDialog.purchaseLink") as string,
    cancelButton: i18n.t("common.close") as string,
    okVariant: "primary",
  });

  if (shouldOpenPurchase) {
    const url = router.resolve({ name: "purchaseFish" }).href;
    window.open(url, "_blank");
  }

  return true;
}
